import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormGroup, FormsModule, NonNullableFormBuilder, ReactiveFormsModule, ValidationErrors, ValidatorFn } from '@angular/forms';
import { MatFormFieldModule, MatLabel } from '@angular/material/form-field';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { startWith, Subject, takeUntil } from 'rxjs';
import { Constants } from '../../../constants/constants';
import { ReviewService } from '../../../services/review.service';
import { MatRadioModule } from '@angular/material/radio';
import { MatInputModule } from '@angular/material/input';
import { SiRadioButtonComponent } from '../../Si-Web-Toolkit/si-radio-button/si-radio-button.component';
import { SiCheckBoxGroupComponent } from '../../Si-Web-Toolkit/si-check-box-group/si-check-box-group.component';
import { SiTextAreaComponent } from '../../Si-Web-Toolkit/si-text-area/si-text-area.component';
import { MatIcon } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CostReviewElectricalScopeOfWork } from '../../../models/CostReviewElectricalScopeOfWork';
import { CostReviewElectricalDocumentation } from '../../../models/CostReviewElectricalDocumentation';
import { CostReviewElectricalProvidedDocuments } from '../../../models/CostReviewElectricalProvidedDocuments';
import { CostReviewElectricalEquipment } from '../../../models/CostReviewElectricalEquipment';
import { CostReviewElectricalSafety } from '../../../models/CostReviewElectricalSafety';
import { CostReviewElectricalSiteAccessibility } from '../../../models/CostReviewElectricalSiteAccessibility';
import { CostReviewElectricalLaborAndMaterials } from '../../../models/CostReviewElectricalLaborAndMaterials';
import { CostReviewElectricalSubcontractors } from '../../../models/CostReviewElectricalSubcontractors';
import { CostReviewElectricalComments } from '../../../models/CostReviewElectricalComments';
import { CostReviewElectricalAttributes } from '../../../models/CostReviewElectricalAttributes';
import { IElectricalScopeOfWorkForm } from '../../../models/SolChecklistInterfaces/IElectricalScopeOfWorkForm';
import { IElectricalDocumentationForm } from '../../../models/SolChecklistInterfaces/IElectricalDocumentationForm';
import { IElectricalSafetyForm } from '../../../models/SolChecklistInterfaces/IElectricalSafetyForm';
import { IElectricalSiteAccessibilityForm } from '../../../models/SolChecklistInterfaces/IElectricalSiteAccessibilityForm';
import { IElectricalLaborAndMaterialsForm } from '../../../models/SolChecklistInterfaces/IElectricalLaborAndMaterialsForm';
import { IElectricalEquipmentForm } from '../../../models/SolChecklistInterfaces/IElectricalEquipmentForm';
import { IElectricalProvidedDocumentsForm } from '../../../models/SolChecklistInterfaces/IElectricalProvidedDocumentsForm';
import { IElectricalSubcontractorsForm } from '../../../models/SolChecklistInterfaces/IElectricalSubcontractorsForm';
import { IElectricalCommentsForm } from '../../../models/SolChecklistInterfaces/IElectricalCommentsForm';
import { ReviewDetailTabsService } from '../../../lib/review-detail-tabs.service';
import { CommonDataService } from '../../../services/common-data.service';
import { TrackChangesDirective } from '../../../Directives/track-changes.directive';

@Component({
  selector: 'lib-sol-checklist-electrical',
  imports: [
    MatLabel,
    CommonModule,
    TranslatePipe,
    SiRadioButtonComponent,
    FormsModule,
    ReactiveFormsModule,
    MatRadioModule,
    MatFormFieldModule,
    MatInputModule,
    SiCheckBoxGroupComponent,
    SiTextAreaComponent,
    MatIcon,
    MatTooltipModule,
    TrackChangesDirective
  ],
  templateUrl: './sol-checklist-electrical.component.html',
  styleUrl: './sol-checklist-electrical.component.scss',
  standalone: true
})
export class SolChecklistElectricalComponent implements OnInit, OnDestroy {

  constants = Constants;
  isReadMode = false;
  specialEquipmentOptions: string[] = [];
  isDisabled = false;
  private readonly destroy$ = new Subject<void>();

  // Data objects actually used by forms
  costReviewScopeOfWork: CostReviewElectricalScopeOfWork = new CostReviewElectricalScopeOfWork();
  costReviewDocumentation: CostReviewElectricalDocumentation = new CostReviewElectricalDocumentation();
  costReviewSafety: CostReviewElectricalSafety = new CostReviewElectricalSafety();
  costReviewSiteAccessibility: CostReviewElectricalSiteAccessibility = new CostReviewElectricalSiteAccessibility();
  costReviewLaborAndMaterials: CostReviewElectricalLaborAndMaterials = new CostReviewElectricalLaborAndMaterials();
  costReviewEquipment: CostReviewElectricalEquipment = new CostReviewElectricalEquipment();
  costReviewProvidedDocuments: CostReviewElectricalProvidedDocuments = new CostReviewElectricalProvidedDocuments();
  costReviewSubcontractors: CostReviewElectricalSubcontractors = new CostReviewElectricalSubcontractors();
  costReviewComments: CostReviewElectricalComments = new CostReviewElectricalComments();

  // FormGroups
  scopeOfWorkFormGroup!: FormGroup<IElectricalScopeOfWorkForm>;
  documentationFormGroup!: FormGroup<IElectricalDocumentationForm>;
  safetyFormGroup!: FormGroup<IElectricalSafetyForm>;
  siteAccessibilityFormGroup!: FormGroup<IElectricalSiteAccessibilityForm>;
  laborAndMaterialsFormGroup!: FormGroup<IElectricalLaborAndMaterialsForm>;
  equipmentFormGroup!: FormGroup<IElectricalEquipmentForm>;
  providedDocumentsFormGroup!: FormGroup<IElectricalProvidedDocumentsForm>;
  subcontractorsFormGroup!: FormGroup<IElectricalSubcontractorsForm>;
  commentsFormGroup!: FormGroup<IElectricalCommentsForm>;

  constructor(
    private readonly reviewService: ReviewService,
    private readonly formBuilder: NonNullableFormBuilder,
    private readonly reviewDetailsTabService: ReviewDetailTabsService,
    private readonly commonDataService: CommonDataService
  ) { }

  ngOnInit() {
    this.getReviewLibraryState();
     this.setupReadOnlyMode();
    this.initializeForms();
    this.specialEquipmentOptions = this.getParameterOptions(this.constants.SPECIAL_EQUIPMENT);
    this.prefillAllFormGroups();
    this.subscribeToReviewFormDataSyncRequest();
  }

  /** Allows the host app to force this tab to push its latest data without waiting for tab switch/destroy. */
  private subscribeToReviewFormDataSyncRequest(): void {
    this.reviewDetailsTabService.getReviewFormDataSyncRequested$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.pushFormDataToService());
  }
  private setupReadOnlyMode(): void {
    this.commonDataService.libraryReadOnlyMode$
      .pipe(takeUntil(this.destroy$))
      .subscribe((readOnly: boolean) => {
        const isLibraryUsedInSQ = this.reviewDetailsTabService.getIsLibraryUsedInSQ();
        if (isLibraryUsedInSQ) {
          this.isDisabled = readOnly;
          this.isReadMode = false;
        } else {
          this.isReadMode = readOnly;
        }
      });
  }

  ngOnDestroy(): void {
    this.pushFormDataToService();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeForms() {
    const costReviewData = this.reviewService.costReviewDetails.reviewObject as CostReviewElectricalAttributes;
    
    this.costReviewScopeOfWork = costReviewData.costReviewScopeOfWork ?? new CostReviewElectricalScopeOfWork();
    this.costReviewDocumentation = costReviewData.costReviewDocumentation ?? new CostReviewElectricalDocumentation();
    this.costReviewSafety = costReviewData.costReviewSafety ?? new CostReviewElectricalSafety();
    this.costReviewSiteAccessibility = costReviewData.costReviewSiteAccessibility ?? new CostReviewElectricalSiteAccessibility();
    this.costReviewLaborAndMaterials = costReviewData.costReviewLaborAndMaterials ?? new CostReviewElectricalLaborAndMaterials();
    this.costReviewEquipment = costReviewData.costReviewEquipment ?? new CostReviewElectricalEquipment();
    this.costReviewProvidedDocuments = costReviewData.costReviewProvidedDocuments ?? new CostReviewElectricalProvidedDocuments();
    this.costReviewSubcontractors = costReviewData.costReviewSubcontractors ?? new CostReviewElectricalSubcontractors();
    this.costReviewComments = costReviewData.costReviewComments ?? new CostReviewElectricalComments();
  }

  prefillAllFormGroups() {
    this.prefillScopeOfWorkForm();
    this.prefillDocumentationForm();
    this.prefillSafetyForm();
    this.prefillSiteAccessibilityForm();
    this.prefillLaborAndMaterialsForm();
    this.prefillEquipmentForm();
    this.prefillProvidedDocumentsForm();
    this.prefillSubcontractorsForm();
    this.prefillCommentsForm();
  }

  prefillScopeOfWorkForm() {
    this.scopeOfWorkFormGroup = this.formBuilder.group({
      bomRequired: this.formBuilder.control(
        this.getParameterValues(this.constants.YES_NO, this.costReviewScopeOfWork.bomRequired)),
      bomDocumentReceived: this.formBuilder.control(
        this.getParameterValues(this.constants.YES_NO, this.costReviewScopeOfWork.bomDocumentReceived)),
      bomProvidedBy: this.formBuilder.control(
        this.getParameterValues(this.constants.SIEMENS_THIRD_PARTY, this.costReviewScopeOfWork.bomProvidedBy)),
      writtenSpecificationsAvailable: this.formBuilder.control(
        this.getParameterValues(this.constants.YES_NO, this.costReviewScopeOfWork.writtenSpecificationsAvailable)),
      specificationProvider: this.formBuilder.control(
        this.getParameterValues(this.constants.SIEMENS_THIRD_PARTY, this.costReviewScopeOfWork.specificationProvider)),
      specificationDocumentAvailable: this.formBuilder.control(
        this.getParameterValues(this.constants.SIEMENS_THIRD_PARTY, this.costReviewScopeOfWork.specificationDocumentAvailable)),
      ddrKnown: this.formBuilder.control(
        this.getParameterValues(this.constants.YES_NO_NA, this.costReviewScopeOfWork.ddrKnown)),
      engineeringRequired: this.formBuilder.control(
        this.getParameterValues(this.constants.YES_NO, this.costReviewScopeOfWork.engineeringRequired)),
      engineeringProvidedBy: this.formBuilder.control(
        this.getParameterValues(this.constants.SIEMENS_THIRD_PARTY, this.costReviewScopeOfWork.engineeringProvidedBy)),
      peStampRequired: this.formBuilder.control(
        this.getParameterValues(this.constants.YES_NO, this.costReviewScopeOfWork.peStampRequired)),
      technicalSolutionReviewed: this.formBuilder.control(
        this.getParameterValues(this.constants.YES_NO_NA, this.costReviewScopeOfWork.technicalSolutionReviewed))
    });

    this.setupDependentFieldRule(
      this.scopeOfWorkFormGroup,
      'bomRequired',
      ['bomDocumentReceived', 'bomProvidedBy'],
      (value) => this.isNoSelection(value),
      'bomRequiredDependency'
    );
    this.setupDependentFieldRule(
      this.scopeOfWorkFormGroup,
      'writtenSpecificationsAvailable',
      ['specificationProvider', 'specificationDocumentAvailable'],
      (value) => this.isNoSelection(value),
      'writtenSpecificationsAvailableDependency'
    );

    this.setupDependentFieldRule(
      this.scopeOfWorkFormGroup,
      'engineeringRequired',
      ['engineeringProvidedBy', 'peStampRequired'],
      (value) => this.isNoSelection(value),
      'engineeringRequiredDependency'
    );
  }

  private setupDependentFieldRule(
    formGroup: FormGroup,
    parentControlName: string,
    childControlNames: string[],
    shouldDisableChildren: (parentValue: string) => boolean,
    validationKey: string
  ): void {
    const existingValidator = formGroup.validator;
    const dependencyValidator = this.createDependentFieldValidator(
      formGroup,
      parentControlName,
      childControlNames,
      shouldDisableChildren,
      validationKey
    );

    if (existingValidator) {
      formGroup.setValidators([existingValidator, dependencyValidator]);
    } else {
      formGroup.setValidators(dependencyValidator);
    }

    const parentControl = formGroup.get(parentControlName);
    if (!parentControl) {
      return;
    }

    const syncState = (): void => {
      this.applyDependentFieldState(formGroup, parentControlName, childControlNames, shouldDisableChildren);
      formGroup.updateValueAndValidity({ emitEvent: false });
    };

    parentControl.valueChanges
      .pipe(startWith(parentControl.value), takeUntil(this.destroy$))
      .subscribe(() => syncState());
  }

  private createDependentFieldValidator(
    formGroup: FormGroup,
    parentControlName: string,
    childControlNames: string[],
    shouldDisableChildren: (parentValue: string) => boolean,
    validationKey: string
  ): ValidatorFn {
    return (): ValidationErrors | null => {
      const parentValue = (formGroup.get(parentControlName)?.value as string) || Constants.EMPTY_STRING;
      if (!shouldDisableChildren(parentValue)) {
        return null;
      }

      const hasEnabledChild = childControlNames.some((childControlName) => {
        const childControl = formGroup.get(childControlName);
        return !!childControl && childControl.enabled;
      });

      return hasEnabledChild ? { [validationKey]: true } : null;
    };
  }

  private applyDependentFieldState(
    formGroup: FormGroup,
    parentControlName: string,
    childControlNames: string[],
    shouldDisableChildren: (parentValue: string) => boolean
  ): void {
    const parentValue = (formGroup.get(parentControlName)?.value as string) || Constants.EMPTY_STRING;
    const disableChildren = shouldDisableChildren(parentValue);

    for (const childControlName of childControlNames) {
      const childControl = formGroup.get(childControlName);
      if (!childControl) {
        continue;
      }

      if (disableChildren) {
        childControl.disable({ emitEvent: false });
      } else {
        childControl.enable({ emitEvent: false });
      }
    }
  }

  private isNoSelection(value: string): boolean {
    const normalizedValue = (value || Constants.EMPTY_STRING).trim().toLowerCase();
    if (!normalizedValue) {
      return false;
    }

    const noValues = [
      this.constants.NO,
      this.constants.FRENCH_NO,
      this.getParameterValues(this.constants.YES_NO, this.constants.TWO)
    ].map((item) => (item || Constants.EMPTY_STRING).trim().toLowerCase());

    if (noValues.includes(normalizedValue)) {
      return true;
    }

    const noCode = Number(this.constants.TWO) || 0;
    const selectedCode = this.reviewService.getCostReviewParametersCode(this.constants.YES_NO, value);
    return selectedCode !== undefined && selectedCode === noCode;
  }

  prefillDocumentationForm() {
    this.documentationFormGroup = this.formBuilder.group({
      asBuiltDrawingsProvided: this.formBuilder.control(
        this.getParameterValues(this.constants.YES_NO, this.costReviewDocumentation.asBuiltDrawingsProvided)),
      oneLineDrawingsProvided: this.formBuilder.control(
        this.getParameterValues(this.constants.YES_NO, this.costReviewDocumentation.oneLineDrawingsProvided)),
      wiringDiagramsProvided: this.formBuilder.control(
        this.getParameterValues(this.constants.YES_NO, this.costReviewDocumentation.wiringDiagramsProvided)),
      equipmentSettingsOrPowerSystemStudiesProvided: this.formBuilder.control(
        this.getParameterValues(this.constants.YES_NO, this.costReviewDocumentation.equipmentSettingsOrPowerSystemStudiesProvided)),
      photographsProvided: this.formBuilder.control(
        this.getParameterValues(this.constants.YES_NO, this.costReviewDocumentation.photographsProvided)),
      nameplatesProvided: this.formBuilder.control(
        this.getParameterValues(this.constants.YES_NO, this.costReviewDocumentation.nameplatesProvided)),
      documentationComments: this.formBuilder.control(this.costReviewDocumentation.documentationComments || '')
    });
  }

  prefillSafetyForm() {
    this.safetyFormGroup = this.formBuilder.group({
      customerSiteSafetyRequired: this.formBuilder.control(
        this.getParameterValues(this.constants.YES_NO, this.costReviewSafety.customerSiteSafetyRequired)),
      safetyObserverRequired: this.formBuilder.control(
        this.getParameterValues(this.constants.YES_NO, this.costReviewSafety.safetyObserverRequired)),
      safetyObserverSuppliedBy: this.formBuilder.control(
        this.getParameterValues(this.constants.SIEMENS_CUSTOMER, this.costReviewSafety.safetyObserverSuppliedBy)),
      outageRequired: this.formBuilder.control(
        this.getParameterValues(this.constants.YES_NO, this.costReviewSafety.outageRequired)),
      temporaryPowerRequired: this.formBuilder.control(
        this.getParameterValues(this.constants.YES_NO, this.costReviewSafety.temporaryPowerRequired)),
      hazardousMaterialsPresent: this.formBuilder.control(
        this.getParameterValues(this.constants.YES_NO, this.costReviewSafety.hazardousMaterialsPresent))
    });

    this.setupDependentFieldRule(
      this.safetyFormGroup,
      'safetyObserverRequired',
      ['safetyObserverSuppliedBy'],
      (value) => this.isNoSelection(value),
      'safetyObserverRequiredDependency'
    );
  }

  prefillSiteAccessibilityForm() {
    this.siteAccessibilityFormGroup = this.formBuilder.group({
      escortsRequired: this.formBuilder.control(
        this.getParameterValues(this.constants.YES_NO, this.costReviewSiteAccessibility.escortsRequired)),
      securityAccessRequired: this.formBuilder.control(
        this.getParameterValues(this.constants.YES_NO, this.costReviewSiteAccessibility.securityAccessRequired)),
      timeOfDayRequired: this.formBuilder.control(
        this.getParameterValues(this.constants.YES_NO, this.costReviewSiteAccessibility.timeOfDayRequired)),
      specialConsiderationsRequired: this.formBuilder.control(
        this.getParameterValues(this.constants.YES_NO, this.costReviewSiteAccessibility.specialConsiderationsRequired))
    });
  }

  prefillLaborAndMaterialsForm() {
    this.laborAndMaterialsFormGroup = this.formBuilder.group({
      manpowerStaffingInPlace: this.formBuilder.control(
        this.getParameterValues(this.constants.YES_NO, this.costReviewLaborAndMaterials.manpowerStaffingInPlace)),
      travelIsRequired: this.formBuilder.control(
        this.getParameterValues(this.constants.YES_NO, this.costReviewLaborAndMaterials.travelIsRequired)),
      costsIncludedInEstimate: this.formBuilder.control(
        this.getParameterValues(this.constants.YES_NO, this.costReviewLaborAndMaterials.costsIncludedInEstimate)),
      materialPricingAndDeliveryIncluded: this.formBuilder.control(
        this.getParameterValues(this.constants.YES_NO, this.costReviewLaborAndMaterials.materialPricingAndDeliveryIncluded)),
      siemensMaterialPricingConfirmed: this.formBuilder.control(
        this.getParameterValues(this.constants.YES_NO, this.costReviewLaborAndMaterials.siemensMaterialPricingConfirmed)),
      siemensMaterialDeliveryDateEstimated: this.formBuilder.control(
        this.getParameterValues(this.constants.YES_NO, this.costReviewLaborAndMaterials.siemensMaterialDeliveryDateEstimated)),
      thirdPartyMaterialPricingConfirmed: this.formBuilder.control(
        this.getParameterValues(this.constants.YES_NO, this.costReviewLaborAndMaterials.thirdPartyMaterialPricingConfirmed)),
      thirdPartyMaterialDeliveryDateEstimated: this.formBuilder.control(
        this.getParameterValues(this.constants.YES_NO, this.costReviewLaborAndMaterials.thirdPartyMaterialDeliveryDateEstimated)),
      workHourRequirements: this.formBuilder.control(
        this.getParameterValues(this.constants.YES_NO, this.costReviewLaborAndMaterials.workHourRequirements)),
      progressMeetingsRequired: this.formBuilder.control(
        this.getParameterValues(this.constants.YES_NO, this.costReviewLaborAndMaterials.progressMeetingsRequired)),
      unionConsiderations: this.formBuilder.control(
        this.getParameterValues(this.constants.YES_NO, this.costReviewLaborAndMaterials.unionConsiderations))
    });

    this.setupDependentFieldRule(
      this.laborAndMaterialsFormGroup,
      'travelIsRequired',
      ['costsIncludedInEstimate'],
      (value) => this.isNoSelection(value),
      'travelIsRequiredDependency'
    );

    this.setupDependentFieldRule(
      this.laborAndMaterialsFormGroup,
      'materialPricingAndDeliveryIncluded',
      ['siemensMaterialPricingConfirmed', 'siemensMaterialDeliveryDateEstimated', 'thirdPartyMaterialPricingConfirmed', 'thirdPartyMaterialDeliveryDateEstimated'],
      (value) => this.isNoSelection(value),
      'materialPricingAndDeliveryIncludedDependency'
    );
  }

  prefillEquipmentForm() {
    this.equipmentFormGroup = this.formBuilder.group({
      toolsAndTestEquipmentRequired: this.formBuilder.control(
        this.getParameterValues(this.constants.YES_NO, this.costReviewEquipment.toolsAndTestEquipmentRequired)),
      equipmentRentalEtcEstimated: this.formBuilder.control(
        this.getParameterValues(this.constants.YES_NO, this.costReviewEquipment.equipmentRentalEtcEstimated)),
      specialEquipmentRequired: this.formBuilder.control(
        this.getParameterValues(this.constants.YES_NO, this.costReviewEquipment.specialEquipmentRequired)),
      specialEquipmentList: this.formBuilder.control(this.getParameterValuesArray(this.constants.SPECIAL_EQUIPMENT, this.costReviewEquipment.specialEquipmentList)),
      responsibleForProviding: this.formBuilder.control(
        this.getParameterValues(this.constants.SIEMENS_THIRDPARTY_CUSTOMER, this.costReviewEquipment.responsibleForProviding))
    });

    this.setupDependentFieldRule(
      this.equipmentFormGroup,
      'toolsAndTestEquipmentRequired',
      ['equipmentRentalEtcEstimated'],
      (value) => this.isNoSelection(value),
      'equipmentRentalEtcEstimatedDependency'
    );

  }

  prefillProvidedDocumentsForm() {
    this.providedDocumentsFormGroup = this.formBuilder.group({
      bookingPackageSigned: this.formBuilder.control(
        this.getParameterValues(this.constants.YES_NO, this.costReviewProvidedDocuments.bookingPackageSigned)),
      scopeOfWorkReviewedAndValidated: this.formBuilder.control(
        this.getParameterValues(this.constants.YES_NO, this.costReviewProvidedDocuments.scopeOfWorkReviewedAndValidated))
    });
  }

  prefillSubcontractorsForm() {
    this.subcontractorsFormGroup = this.formBuilder.group({
      subcontractorTermsAndConditions: this.formBuilder.control(
        this.getParameterValues(this.constants.YES_NO_NA, this.costReviewSubcontractors.subcontractorTermsAndConditions)),
      vendorPricingConfirmed: this.formBuilder.control(
        this.getParameterValues(this.constants.YES_NO_NA, this.costReviewSubcontractors.vendorPricingConfirmed)),
      additionalMaterialPriceAndDeliveryConfirmed: this.formBuilder.control(
        this.getParameterValues(this.constants.YES_NO_NA, this.costReviewSubcontractors.additionalMaterialPriceAndDeliveryConfirmed))
    });
  }

  prefillCommentsForm() {
    this.commentsFormGroup = this.formBuilder.group({
      comments: this.formBuilder.control(this.costReviewComments.comments || '')
    });
  }

  getParameterValues(key: string, value: string | number | undefined) {
    value ??= Constants.EMPTY_STRING;
    return this.reviewService.getCostReviewParametersDescription(key, value);
  }

  getParameterValuesForArray(key: string, values: Array<string | number> | undefined) {
    values ??= [];
    return this.reviewService.getCostReviewParametersDescriptionForArray(key, values);
  }

  getParameterValuesArray(key: string, values: Array<string | number> | undefined): string[] {
    return this.getParameterValuesForArray(key, values)
      .split(', ')
      .filter((value) => !!value);
  }

  private getParameterOptions(parameter: string): string[] {
    const prefix = `${parameter}_`;
    return this.reviewService.costReviewParameters
      .filter((item) => item.parameterID.startsWith(prefix))
      .sort((firstItem, secondItem) => {
        const firstCode = Number(firstItem.parameterID.substring(prefix.length));
        const secondCode = Number(secondItem.parameterID.substring(prefix.length));
        return firstCode - secondCode;
      })
      .map((item) => item.description)
      .filter((description) => !!description);
  }

  private pushFormDataToService(): void {
    const reviewDetails = this.reviewService.costReviewDetails;
    const reviewObject = reviewDetails?.reviewObject as CostReviewElectricalAttributes | undefined;
    if (!reviewObject) {
      return;
    }

    this.ensureElectricalReviewObjects(reviewObject);

    this.saveScopeOfWorkFormData(reviewObject);
    this.saveDocumentationFormData(reviewObject);
    this.saveSafetyFormData(reviewObject);
    this.saveSiteAccessibilityFormData(reviewObject);
    this.saveLaborAndMaterialsFormData(reviewObject);
    this.saveEquipmentFormData(reviewObject);
    this.saveProvidedDocumentsFormData(reviewObject);
    this.saveSubcontractorsFormData(reviewObject);
    this.saveCommentsFormData(reviewObject);
  }

  private ensureElectricalReviewObjects(reviewObject: CostReviewElectricalAttributes): void {
    reviewObject.costReviewScopeOfWork ??= new CostReviewElectricalScopeOfWork();
    reviewObject.costReviewDocumentation ??= new CostReviewElectricalDocumentation();
    reviewObject.costReviewSafety ??= new CostReviewElectricalSafety();
    reviewObject.costReviewSiteAccessibility ??= new CostReviewElectricalSiteAccessibility();
    reviewObject.costReviewLaborAndMaterials ??= new CostReviewElectricalLaborAndMaterials();
    reviewObject.costReviewEquipment ??= new CostReviewElectricalEquipment();
    reviewObject.costReviewProvidedDocuments ??= new CostReviewElectricalProvidedDocuments();
    reviewObject.costReviewSubcontractors ??= new CostReviewElectricalSubcontractors();
    reviewObject.costReviewComments ??= new CostReviewElectricalComments();
  }

  private saveScopeOfWorkFormData(reviewObject: CostReviewElectricalAttributes): void {
    if (!this.scopeOfWorkFormGroup || !reviewObject.costReviewScopeOfWork) {
      return;
    }

    const formData = this.scopeOfWorkFormGroup.getRawValue();
    const target = reviewObject.costReviewScopeOfWork;

    target.bomRequired = this.getParameterCode(this.constants.YES_NO, formData.bomRequired, target.bomRequired);
    target.bomDocumentReceived = this.getParameterCode(this.constants.YES_NO, formData.bomDocumentReceived, target.bomDocumentReceived);
    target.bomProvidedBy = this.getParameterCode(this.constants.SIEMENS_THIRD_PARTY, formData.bomProvidedBy, target.bomProvidedBy);
    target.writtenSpecificationsAvailable = this.getParameterCode(this.constants.YES_NO, formData.writtenSpecificationsAvailable, target.writtenSpecificationsAvailable);
    target.specificationProvider = this.getParameterCode(this.constants.SIEMENS_THIRD_PARTY, formData.specificationProvider, target.specificationProvider);
    target.specificationDocumentAvailable = this.getParameterCode(this.constants.SIEMENS_THIRD_PARTY, formData.specificationDocumentAvailable, target.specificationDocumentAvailable);
    target.ddrKnown = this.getParameterCode(this.constants.YES_NO_NA, formData.ddrKnown, target.ddrKnown);
    target.engineeringRequired = this.getParameterCode(this.constants.YES_NO, formData.engineeringRequired, target.engineeringRequired);
    target.engineeringProvidedBy = this.getParameterCode(this.constants.SIEMENS_THIRD_PARTY, formData.engineeringProvidedBy, target.engineeringProvidedBy);
    target.peStampRequired = this.getParameterCode(this.constants.YES_NO, formData.peStampRequired, target.peStampRequired);
    target.technicalSolutionReviewed = this.getParameterCode(this.constants.YES_NO_NA, formData.technicalSolutionReviewed, target.technicalSolutionReviewed);
  }

  private saveDocumentationFormData(reviewObject: CostReviewElectricalAttributes): void {
    if (!this.documentationFormGroup || !reviewObject.costReviewDocumentation) {
      return;
    }

    const formData = this.documentationFormGroup.getRawValue();
    const target = reviewObject.costReviewDocumentation;

    target.asBuiltDrawingsProvided = this.getParameterCode(this.constants.YES_NO, formData.asBuiltDrawingsProvided, target.asBuiltDrawingsProvided);
    target.oneLineDrawingsProvided = this.getParameterCode(this.constants.YES_NO, formData.oneLineDrawingsProvided, target.oneLineDrawingsProvided);
    target.wiringDiagramsProvided = this.getParameterCode(this.constants.YES_NO, formData.wiringDiagramsProvided, target.wiringDiagramsProvided);
    target.equipmentSettingsOrPowerSystemStudiesProvided = this.getParameterCode(this.constants.YES_NO, formData.equipmentSettingsOrPowerSystemStudiesProvided, target.equipmentSettingsOrPowerSystemStudiesProvided);
    target.photographsProvided = this.getParameterCode(this.constants.YES_NO, formData.photographsProvided, target.photographsProvided);
    target.nameplatesProvided = this.getParameterCode(this.constants.YES_NO, formData.nameplatesProvided, target.nameplatesProvided);
    target.documentationComments = formData.documentationComments || Constants.EMPTY_STRING;
  }

  private saveSafetyFormData(reviewObject: CostReviewElectricalAttributes): void {
    if (!this.safetyFormGroup || !reviewObject.costReviewSafety) {
      return;
    }

    const formData = this.safetyFormGroup.getRawValue();
    const target = reviewObject.costReviewSafety;

    target.customerSiteSafetyRequired = this.getParameterCode(this.constants.YES_NO, formData.customerSiteSafetyRequired, target.customerSiteSafetyRequired);
    target.safetyObserverRequired = this.getParameterCode(this.constants.YES_NO, formData.safetyObserverRequired, target.safetyObserverRequired);
    target.safetyObserverSuppliedBy = this.getParameterCode(this.constants.SIEMENS_CUSTOMER, formData.safetyObserverSuppliedBy, target.safetyObserverSuppliedBy);
    target.outageRequired = this.getParameterCode(this.constants.YES_NO, formData.outageRequired, target.outageRequired);
    target.temporaryPowerRequired = this.getParameterCode(this.constants.YES_NO, formData.temporaryPowerRequired, target.temporaryPowerRequired);
    target.hazardousMaterialsPresent = this.getParameterCode(this.constants.YES_NO, formData.hazardousMaterialsPresent, target.hazardousMaterialsPresent);
  }

  private saveSiteAccessibilityFormData(reviewObject: CostReviewElectricalAttributes): void {
    if (!this.siteAccessibilityFormGroup || !reviewObject.costReviewSiteAccessibility) {
      return;
    }

    const formData = this.siteAccessibilityFormGroup.getRawValue();
    const target = reviewObject.costReviewSiteAccessibility;

    target.escortsRequired = this.getParameterCode(this.constants.YES_NO, formData.escortsRequired, target.escortsRequired);
    target.securityAccessRequired = this.getParameterCode(this.constants.YES_NO, formData.securityAccessRequired, target.securityAccessRequired);
    target.timeOfDayRequired = this.getParameterCode(this.constants.YES_NO, formData.timeOfDayRequired, target.timeOfDayRequired);
    target.specialConsiderationsRequired = this.getParameterCode(this.constants.YES_NO, formData.specialConsiderationsRequired, target.specialConsiderationsRequired);
  }

  private saveLaborAndMaterialsFormData(reviewObject: CostReviewElectricalAttributes): void {
    if (!this.laborAndMaterialsFormGroup || !reviewObject.costReviewLaborAndMaterials) {
      return;
    }

    const formData = this.laborAndMaterialsFormGroup.getRawValue();
    const target = reviewObject.costReviewLaborAndMaterials;

    target.manpowerStaffingInPlace = this.getParameterCode(this.constants.YES_NO, formData.manpowerStaffingInPlace, target.manpowerStaffingInPlace);
    target.travelIsRequired = this.getParameterCode(this.constants.YES_NO, formData.travelIsRequired, target.travelIsRequired);
    target.costsIncludedInEstimate = this.getParameterCode(this.constants.YES_NO, formData.costsIncludedInEstimate, target.costsIncludedInEstimate);
    target.materialPricingAndDeliveryIncluded = this.getParameterCode(this.constants.YES_NO, formData.materialPricingAndDeliveryIncluded, target.materialPricingAndDeliveryIncluded);
    target.siemensMaterialPricingConfirmed = this.getParameterCode(this.constants.YES_NO, formData.siemensMaterialPricingConfirmed, target.siemensMaterialPricingConfirmed);
    target.siemensMaterialDeliveryDateEstimated = this.getParameterCode(this.constants.YES_NO, formData.siemensMaterialDeliveryDateEstimated, target.siemensMaterialDeliveryDateEstimated);
    target.thirdPartyMaterialPricingConfirmed = this.getParameterCode(this.constants.YES_NO, formData.thirdPartyMaterialPricingConfirmed, target.thirdPartyMaterialPricingConfirmed);
    target.thirdPartyMaterialDeliveryDateEstimated = this.getParameterCode(this.constants.YES_NO, formData.thirdPartyMaterialDeliveryDateEstimated, target.thirdPartyMaterialDeliveryDateEstimated);
    target.workHourRequirements = this.getParameterCode(this.constants.YES_NO, formData.workHourRequirements, target.workHourRequirements);
    target.progressMeetingsRequired = this.getParameterCode(this.constants.YES_NO, formData.progressMeetingsRequired, target.progressMeetingsRequired);
    target.unionConsiderations = this.getParameterCode(this.constants.YES_NO, formData.unionConsiderations, target.unionConsiderations);
  }

  private saveEquipmentFormData(reviewObject: CostReviewElectricalAttributes): void {
    if (!this.equipmentFormGroup || !reviewObject.costReviewEquipment) {
      return;
    }

    const formData = this.equipmentFormGroup.getRawValue();
    const target = reviewObject.costReviewEquipment;

    target.toolsAndTestEquipmentRequired = this.getParameterCode(this.constants.YES_NO, formData.toolsAndTestEquipmentRequired, target.toolsAndTestEquipmentRequired);
    target.equipmentRentalEtcEstimated = this.getParameterCode(this.constants.YES_NO, formData.equipmentRentalEtcEstimated, target.equipmentRentalEtcEstimated);
    target.specialEquipmentRequired = this.getParameterCode(this.constants.YES_NO, formData.specialEquipmentRequired, target.specialEquipmentRequired);
    target.specialEquipmentList = this.reviewService.getCostReviewParametersCodesForArray(this.constants.SPECIAL_EQUIPMENT, (formData.specialEquipmentList || []).join(', '));
    target.responsibleForProviding = this.getParameterCode(this.constants.SIEMENS_THIRDPARTY_CUSTOMER, formData.responsibleForProviding, target.responsibleForProviding);
  }

  private saveProvidedDocumentsFormData(reviewObject: CostReviewElectricalAttributes): void {
    if (!this.providedDocumentsFormGroup || !reviewObject.costReviewProvidedDocuments) {
      return;
    }

    const formData = this.providedDocumentsFormGroup.getRawValue();
    const target = reviewObject.costReviewProvidedDocuments;

    target.bookingPackageSigned = this.getParameterCode(this.constants.YES_NO, formData.bookingPackageSigned, target.bookingPackageSigned);
    target.scopeOfWorkReviewedAndValidated = this.getParameterCode(this.constants.YES_NO, formData.scopeOfWorkReviewedAndValidated, target.scopeOfWorkReviewedAndValidated);
  }

  private saveSubcontractorsFormData(reviewObject: CostReviewElectricalAttributes): void {
    if (!this.subcontractorsFormGroup || !reviewObject.costReviewSubcontractors) {
      return;
    }

    const formData = this.subcontractorsFormGroup.getRawValue();
    const target = reviewObject.costReviewSubcontractors;

    target.subcontractorTermsAndConditions = this.getParameterCode(this.constants.YES_NO_NA, formData.subcontractorTermsAndConditions, target.subcontractorTermsAndConditions);
    target.vendorPricingConfirmed = this.getParameterCode(this.constants.YES_NO_NA, formData.vendorPricingConfirmed, target.vendorPricingConfirmed);
    target.additionalMaterialPriceAndDeliveryConfirmed = this.getParameterCode(this.constants.YES_NO_NA, formData.additionalMaterialPriceAndDeliveryConfirmed, target.additionalMaterialPriceAndDeliveryConfirmed);
  }

  private saveCommentsFormData(reviewObject: CostReviewElectricalAttributes): void {
    if (!this.commentsFormGroup || !reviewObject.costReviewComments) {
      return;
    }

    const formData = this.commentsFormGroup.getRawValue();
    reviewObject.costReviewComments.comments = formData.comments || Constants.EMPTY_STRING;
  }

  private getParameterCode(parameter: string, description: string, currentValue: number): number {
    const code = this.reviewService.getCostReviewParametersCode(parameter, description || Constants.EMPTY_STRING);
    return code !== undefined ? code : currentValue;
  }

  private getReviewLibraryState(): void {
    this.isDisabled = this.reviewDetailsTabService.getReviewLibraryDisableFlag();
  }
}

